<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <!-- Button toggle form -->
        <v-btn color="primary" class="mb-4" @click="showAddForm = !showAddForm">
            <v-icon left>mdi-plus-box</v-icon>
            {{ showAddForm ? 'Đóng' : 'Thêm câu hỏi mới' }}
        </v-btn>

        <!-- Form thêm quiz question -->
        <v-card class="mb-6" v-if="showAddForm">
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-plus-circle</v-icon>
                Thêm câu hỏi quiz mới
            </v-card-title>
            <v-card-text>
                <v-form ref="quizQuestionForm" v-model="valid">
                    <v-row>
                        <v-col md="6">
                            <v-select
                                v-model="newQuestion.category_id"
                                :items="quizCategories"
                                item-title="name"
                                item-value="id"
                                label="Danh mục"
                                variant="outlined"
                                density="compact"
                                :rules="[v => !!v || 'Danh mục là bắt buộc']"
                                required>
                            </v-select>
                        </v-col>
                        <v-col md="6">
                            <v-select
                                v-model="newQuestion.difficulty"
                                :items="difficultyOptions"
                                label="Độ khó"
                                variant="outlined"
                                density="compact"
                                :rules="[v => !!v || 'Độ khó là bắt buộc']"
                                required>
                            </v-select>
                        </v-col>
                        <v-col md="12">
                            <v-textarea
                                v-model="newQuestion.question"
                                label="Câu hỏi"
                                placeholder="Nhập nội dung câu hỏi..."
                                variant="outlined"
                                density="compact"
                                rows="3"
                                :rules="[v => !!v || 'Câu hỏi là bắt buộc']"
                                required>
                            </v-textarea>
                        </v-col>
                        <v-col md="12">
                            <v-textarea
                                v-model="newQuestion.code"
                                label="Mã code (Tùy chọn)"
                                placeholder="Nhập mã code nếu có..."
                                variant="outlined"
                                density="compact"
                                rows="4">
                            </v-textarea>
                        </v-col>
                        
                        <!-- Các tùy chọn đáp án -->
                        <v-col md="12">
                            <v-subheader class="px-0 font-weight-medium">Các tùy chọn đáp án</v-subheader>
                            <div v-for="(option, index) in newQuestion.options" :key="index" class="mb-3">
                                <v-row>
                                    <v-col cols="10">
                                        <v-text-field
                                            v-model="newQuestion.options[index]"
                                            :label="`Tùy chọn ${index + 1}`"
                                            variant="outlined"
                                            density="compact"
                                            :rules="[v => !!v || 'Tùy chọn không được để trống']"
                                            required>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="d-flex align-center">
                                        <v-radio-group
                                            v-model="newQuestion.correctAnswer"
                                            class="mt-0"
                                            hide-details>
                                            <v-radio
                                                :value="index"
                                                :label="`Đúng`"
                                                color="success">
                                            </v-radio>
                                        </v-radio-group>
                                        <v-btn
                                            v-if="newQuestion.options.length > 2"
                                            icon
                                            size="small"
                                            color="error"
                                            @click="removeOption(index)"
                                            class="ml-2">
                                            <v-icon>mdi-minus</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                            <v-btn
                                v-if="newQuestion.options.length < 6"
                                color="primary"
                                variant="outlined"
                                @click="addOption"
                                class="mb-4">
                                <v-icon left>mdi-plus</v-icon>
                                Thêm tùy chọn
                            </v-btn>
                        </v-col>

                        <v-col md="12">
                            <v-textarea
                                v-model="newQuestion.explanation"
                                label="Giải thích"
                                placeholder="Nhập giải thích cho đáp án..."
                                variant="outlined"
                                density="compact"
                                rows="3"
                                :rules="[v => !!v || 'Giải thích là bắt buộc']"
                                required>
                            </v-textarea>
                        </v-col>
                        
                        <v-col cols="12" class="d-flex justify-center">
                            <v-btn
                                color="primary"
                                @click="addQuizQuestion"
                                :loading="loading"
                                :disabled="!valid || newQuestion.correctAnswer === null">
                                <v-icon class="mr-2">mdi-content-save</v-icon>
                                Lưu câu hỏi
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Filter và search -->
        <v-card class="mb-4">
            <v-card-text>
                <v-row>
                    <v-col md="4">
                        <v-select
                            v-model="selectedCategoryFilter"
                            :items="categoryFilterOptions"
                            item-title="name"
                            item-value="id"
                            label="Lọc theo danh mục"
                            variant="outlined"
                            density="compact"
                            clearable
                            @update:modelValue="onCategoryFilterChange">
                        </v-select>
                    </v-col>
                    <v-col md="4">
                        <v-select
                            v-model="selectedDifficultyFilter"
                            :items="difficultyFilterOptions"
                            label="Lọc theo độ khó"
                            variant="outlined"
                            density="compact"
                            clearable
                            @update:modelValue="applyFilters">
                        </v-select>
                    </v-col>
                    <v-col md="4">
                        <v-text-field
                            v-model="search"
                            label="Tìm kiếm câu hỏi"
                            @input="onSearchInput"
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            variant="outlined"
                            density="compact">
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Table danh sách questions -->
        <v-card>
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-help-circle</v-icon>
                Danh sách câu hỏi quiz
            </v-card-title>
            <v-card-text>
                <v-table class="elevation-1">
                    <thead>
                        <tr>
                            <th class="text-left">Câu hỏi</th>
                            <th class="text-center">Danh mục</th>
                            <th class="text-center">Độ khó</th>
                            <th class="text-center">Số tùy chọn</th>
                            <th class="text-center">Trạng thái</th>
                            <th class="text-center">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="question in filteredQuestions" :key="question.id">
                            <td class="question-column">
                                {{ truncateText(question.question, 80) }}
                            </td>
                            <td class="text-center">
                                <v-chip size="small" color="primary">
                                    {{ question.category_name }}
                                </v-chip>
                            </td>
                            <td class="text-center">
                                <v-chip 
                                    size="small" 
                                    :color="getDifficultyColor(question.difficulty)">
                                    {{ getDifficultyText(question.difficulty) }}
                                </v-chip>
                            </td>
                            <td class="text-center">{{ question.options ? question.options.length : 0 }}</td>
                            <td class="text-center">
                                <v-chip :color="question.status === 1 ? 'success' : 'error'" size="small">
                                    {{ question.status === 1 ? 'Hoạt động' : 'Tạm ẩn' }}
                                </v-chip>
                            </td>
                            <td class="text-center">
                                <div class="d-flex justify-center">
                                    <v-btn
                                        icon
                                        size="small"
                                        color="info"
                                        @click="viewQuestion(question)"
                                        class="mr-1">
                                        <v-icon>mdi-eye</v-icon>
                                    </v-btn>
                                    <v-btn
                                        icon
                                        size="small"
                                        color="primary"
                                        @click="editQuestion(question)"
                                        class="mr-1">
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn
                                        icon
                                        size="small"
                                        :color="question.status === 0 ? 'success' : 'error'"
                                        @click="toggleStatus(question)"
                                        class="mr-1">
                                        <v-icon>
                                            {{ question.status === 0 ? 'mdi-eye' : 'mdi-eye-off' }}
                                        </v-icon>
                                    </v-btn>
                                    <v-btn
                                        icon
                                        size="small"
                                        color="red"
                                        @click="deleteQuestion(question)">
                                        <v-icon>mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>
        </v-card>

        <!-- Dialog view question detail -->
        <v-dialog v-model="viewDialog" max-width="800" persistent>
            <v-card v-if="viewingQuestion">
                <v-card-title class="text-h6">
                    <v-icon class="mr-2">mdi-eye</v-icon>
                    Chi tiết câu hỏi
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <strong>Câu hỏi:</strong>
                            <p class="mt-2">{{ viewingQuestion.question }}</p>
                        </v-col>
                        <v-col cols="6" v-if="viewingQuestion.code">
                            <strong>Code:</strong>
                            <pre class="mt-2 pa-3 bg-grey-lighten-4">{{ viewingQuestion.code }}</pre>
                        </v-col>
                        <v-col cols="12">
                            <strong>Các tùy chọn:</strong>
                            <ul class="mt-2">
                                <li v-for="(option, index) in viewingQuestion.options" :key="index"
                                    :class="{ 'font-weight-bold text-success': index === viewingQuestion.correctAnswer }">
                                    {{ option }}
                                    <v-icon v-if="index === viewingQuestion.correctAnswer" color="success" size="small" class="ml-2">
                                        mdi-check-circle
                                    </v-icon>
                                </li>
                            </ul>
                        </v-col>
                        <v-col cols="12">
                            <strong>Giải thích:</strong>
                            <p class="mt-2">{{ viewingQuestion.explanation }}</p>
                        </v-col>
                        <v-col cols="6">
                            <strong>Danh mục:</strong> {{ viewingQuestion.category_name }}
                        </v-col>
                        <v-col cols="6">
                            <strong>Độ khó:</strong> {{ getDifficultyText(viewingQuestion.difficulty) }}
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="closeViewDialog">
                        Đóng
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog edit question -->
        <v-dialog v-model="editDialog" max-width="1000" persistent>
            <v-card v-if="editingQuestion.question !== undefined">
                <v-card-title class="text-h6">
                    <v-icon class="mr-2">mdi-pencil</v-icon>
                    Chỉnh sửa câu hỏi
                </v-card-title>
                <v-card-text>
                    <v-form ref="editQuestionForm" v-model="editValid">
                        <v-row>
                            <v-col md="6">
                                <v-select
                                    v-model="editingQuestion.category_id"
                                    :items="quizCategories"
                                    item-title="name"
                                    item-value="id"
                                    label="Danh mục"
                                    variant="outlined"
                                    density="compact"
                                    required>
                                </v-select>
                            </v-col>
                            <v-col md="6">
                                <v-select
                                    v-model="editingQuestion.difficulty"
                                    :items="difficultyOptions"
                                    label="Độ khó"
                                    variant="outlined"
                                    density="compact"
                                    required>
                                </v-select>
                            </v-col>
                            <v-col md="12">
                                <v-textarea
                                    v-model="editingQuestion.question"
                                    label="Câu hỏi"
                                    variant="outlined"
                                    density="compact"
                                    rows="3"
                                    required>
                                </v-textarea>
                            </v-col>
                            <v-col md="12">
                                <v-textarea
                                    v-model="editingQuestion.code"
                                    label="Mã code (Tùy chọn)"
                                    variant="outlined"
                                    density="compact"
                                    rows="4">
                                </v-textarea>
                            </v-col>
                            
                            <!-- Các tùy chọn đáp án -->
                            <v-col md="12">
                                <v-subheader class="px-0 font-weight-medium">Các tùy chọn đáp án</v-subheader>
                                <div v-for="(option, index) in editingQuestion.options" :key="index" class="mb-3">
                                    <v-row>
                                        <v-col cols="10">
                                            <v-text-field
                                                v-model="editingQuestion.options[index]"
                                                :label="`Tùy chọn ${index + 1}`"
                                                variant="outlined"
                                                density="compact"
                                                required>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="2" class="d-flex align-center">
                                            <v-radio-group
                                                v-model="editingQuestion.correctAnswer"
                                                class="mt-0"
                                                hide-details>
                                                <v-radio
                                                    :value="index"
                                                    :label="`Đúng`"
                                                    color="success">
                                                </v-radio>
                                            </v-radio-group>
                                            <v-btn
                                                v-if="editingQuestion.options.length > 2"
                                                icon
                                                size="small"
                                                color="error"
                                                @click="removeEditOption(index)"
                                                class="ml-2">
                                                <v-icon>mdi-minus</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </div>
                                <v-btn
                                    v-if="editingQuestion.options && editingQuestion.options.length < 6"
                                    color="primary"
                                    variant="outlined"
                                    @click="addEditOption"
                                    class="mb-4">
                                    <v-icon left>mdi-plus</v-icon>
                                    Thêm tùy chọn
                                </v-btn>
                            </v-col>

                            <v-col md="12">
                                <v-textarea
                                    v-model="editingQuestion.explanation"
                                    label="Giải thích"
                                    variant="outlined"
                                    density="compact"
                                    rows="3"
                                    required>
                                </v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="closeEditDialog">
                        Hủy
                    </v-btn>
                    <v-btn color="primary" variant="elevated" @click="updateQuestion" :loading="updateLoading">
                        Cập nhật
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog confirm delete -->
        <v-dialog v-model="deleteDialog" max-width="400" persistent>
            <v-card>
                <v-card-title class="text-h6">
                    <v-icon class="mr-2" color="red">mdi-delete-alert</v-icon>
                    Xác nhận xóa
                </v-card-title>
                <v-card-text>
                    Bạn có chắc chắn muốn xóa câu hỏi này? Thao tác này không thể hoàn tác.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="closeDeleteDialog">
                        Hủy
                    </v-btn>
                    <v-btn color="red" variant="elevated" @click="confirmDelete" :loading="deleteLoading">
                        Xóa
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar thông báo -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
import axios from '../http';

export default {
    name: 'QuizQuestion',
    data() {
        return {
            valid: false,
            editValid: false,
            loading: false,
            pageLoading: false,
            updateLoading: false,
            deleteLoading: false,
            showAddForm: false,
            editDialog: false,
            deleteDialog: false,
            viewDialog: false,
            newQuestion: {
                category_id: null,
                question: '',
                options: ['', ''],
                correctAnswer: null,
                difficulty: '',
                explanation: '',
                code: ''
            },
            editingQuestion: {},
            deletingQuestion: null,
            viewingQuestion: null,
            quizQuestions: [],
            filteredQuestions: [],
            quizCategories: [],
            selectedCategoryFilter: null,
            selectedDifficultyFilter: null,
            search: '',
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            difficultyOptions: [
                { title: 'Dễ', value: 'easy' },
                { title: 'Trung bình', value: 'medium' },
                { title: 'Khó', value: 'hard' }
            ],
            difficultyFilterOptions: [
                { title: 'Tất cả', value: null },
                { title: 'Dễ', value: 'easy' },
                { title: 'Trung bình', value: 'medium' },
                { title: 'Khó', value: 'hard' }
            ]
        }
    },
    computed: {
        categoryFilterOptions() {
            return [
                { name: 'Tất cả danh mục', id: null },
                ...this.quizCategories
            ];
        }
    },
    mounted() {
        this.fetchQuizCategories();
        this.fetchQuizQuestions(true);
    },
    methods: {
        async fetchQuizCategories() {
            try {
                const response = await axios.get('/admin/quiz-category');
                this.quizCategories = response.data;
            } catch (error) {
                console.error('Error fetching quiz categories:', error);
                this.showSnackbar('Lỗi khi tải danh sách danh mục', 'error');
            }
        },

        async fetchQuizQuestions(showPageLoading = false) {
            if (showPageLoading) {
                this.pageLoading = true;
            }
            
            try {
                const response = await axios.get('/admin/quiz-question');
                this.quizQuestions = response.data.map(q => ({
                    ...q,
                    options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options
                }));
                this.applyFilters();
            } catch (error) {
                console.error('Error fetching quiz questions:', error);
                this.showSnackbar('Lỗi khi tải danh sách câu hỏi', 'error');
            } finally {
                if (showPageLoading) {
                    this.pageLoading = false;
                }
            }
        },

        async addQuizQuestion() {
            if (!this.valid || this.newQuestion.correctAnswer === null) return;

            this.loading = true;
            try {
                const payload = {
                    category_id: this.newQuestion.category_id,
                    question: this.newQuestion.question,
                    options: this.newQuestion.options.filter(opt => opt.trim() !== ''),
                    correctAnswer: this.newQuestion.correctAnswer,
                    difficulty: this.newQuestion.difficulty,
                    explanation: this.newQuestion.explanation
                };
                if (this.newQuestion.code) {
                    payload.code = this.newQuestion.code;
                }

                const response = await axios.post('/admin/quiz-question', payload);
                const newQ = response.data;
                
                // Find category name
                const category = this.quizCategories.find(c => c.id === newQ.category_id);
                newQ.category_name = category ? category.name : '';
                newQ.options = typeof newQ.options === 'string' ? JSON.parse(newQ.options) : newQ.options;
                
                this.quizQuestions.unshift(newQ);
                this.resetNewQuestionForm();
                this.showAddForm = false;
                this.applyFilters();
                this.showSnackbar('Thêm câu hỏi thành công', 'success');
            } catch (error) {
                console.error('Error adding quiz question:', error);
                this.showSnackbar('Lỗi khi thêm câu hỏi', 'error');
            } finally {
                this.loading = false;
            }
        },

        resetNewQuestionForm() {
            this.newQuestion = {
                category_id: null,
                question: '',
                options: ['', ''],
                correctAnswer: null,
                difficulty: '',
                explanation: '',
                code: ''
            };
            if (this.$refs.quizQuestionForm) {
                this.$refs.quizQuestionForm.resetValidation();
            }
        },

        addOption() {
            if (this.newQuestion.options.length < 6) {
                this.newQuestion.options.push('');
            }
        },

        removeOption(index) {
            if (this.newQuestion.options.length > 2) {
                this.newQuestion.options.splice(index, 1);
                // Adjust correct answer if needed
                if (this.newQuestion.correctAnswer >= index) {
                    this.newQuestion.correctAnswer = this.newQuestion.correctAnswer > 0 ? this.newQuestion.correctAnswer - 1 : 0;
                }
            }
        },

        addEditOption() {
            if (this.editingQuestion.options.length < 6) {
                this.editingQuestion.options.push('');
            }
        },

        removeEditOption(index) {
            if (this.editingQuestion.options.length > 2) {
                this.editingQuestion.options.splice(index, 1);
                // Adjust correct answer if needed
                if (this.editingQuestion.correctAnswer >= index) {
                    this.editingQuestion.correctAnswer = this.editingQuestion.correctAnswer > 0 ? this.editingQuestion.correctAnswer - 1 : 0;
                }
            }
        },

        viewQuestion(question) {
            this.viewingQuestion = question;
            this.viewDialog = true;
        },

        editQuestion(question) {
            this.editingQuestion = { 
                ...question,
                options: [...question.options]
            };
            this.editDialog = true;
        },

        async updateQuestion() {
            if (!this.editValid) return;

            this.updateLoading = true;
            try {
                const payload = {
                    category_id: this.editingQuestion.category_id,
                    question: this.editingQuestion.question,
                    options: this.editingQuestion.options.filter(opt => opt.trim() !== ''),
                    correctAnswer: this.editingQuestion.correctAnswer,
                    difficulty: this.editingQuestion.difficulty,
                    explanation: this.editingQuestion.explanation
                };
                if (this.editingQuestion.code) {
                    payload.code = this.editingQuestion.code;
                }

                await axios.put(`/admin/quiz-question/${this.editingQuestion.id}`, payload);
                
                const index = this.quizQuestions.findIndex(q => q.id === this.editingQuestion.id);
                if (index !== -1) {
                    // Find category name
                    const category = this.quizCategories.find(c => c.id === payload.category_id);
                    this.quizQuestions[index] = { 
                        ...this.quizQuestions[index], 
                        ...payload,
                        category_name: category ? category.name : ''
                    };
                }
                
                this.editDialog = false;
                this.applyFilters();
                this.showSnackbar('Cập nhật câu hỏi thành công', 'success');
            } catch (error) {
                console.error('Error updating quiz question:', error);
                this.showSnackbar('Lỗi khi cập nhật câu hỏi', 'error');
            } finally {
                this.updateLoading = false;
            }
        },

        async toggleStatus(question) {
            try {
                const newStatus = question.status === 1 ? 0 : 1;
                await axios.put(`/admin/quiz-question/${question.id}/status`, { status: newStatus });
                
                const index = this.quizQuestions.findIndex(q => q.id === question.id);
                if (index !== -1) {
                    this.quizQuestions[index].status = newStatus;
                }
                
                this.applyFilters();
                this.showSnackbar(`${newStatus === 1 ? 'Kích hoạt' : 'Ẩn'} câu hỏi thành công`, 'success');
            } catch (error) {
                console.error('Error toggling status:', error);
                this.showSnackbar('Lỗi khi thay đổi trạng thái', 'error');
            }
        },

        deleteQuestion(question) {
            this.deletingQuestion = question;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            if (!this.deletingQuestion) return;

            this.deleteLoading = true;
            try {
                await axios.delete(`/admin/quiz-question/${this.deletingQuestion.id}`);
                
                const index = this.quizQuestions.findIndex(q => q.id === this.deletingQuestion.id);
                if (index !== -1) {
                    this.quizQuestions.splice(index, 1);
                }
                
                this.deleteDialog = false;
                this.applyFilters();
                this.showSnackbar('Xóa câu hỏi thành công', 'success');
            } catch (error) {
                console.error('Error deleting quiz question:', error);
                this.showSnackbar('Lỗi khi xóa câu hỏi', 'error');
            } finally {
                this.deleteLoading = false;
            }
        },

        onCategoryFilterChange() {
            this.applyFilters();
        },

        onSearchInput() {
            this.applyFilters();
        },

        applyFilters() {
            let filtered = [...this.quizQuestions];
            
            if (this.selectedCategoryFilter) {
                filtered = filtered.filter(q => q.category_id === this.selectedCategoryFilter);
            }
            
            if (this.selectedDifficultyFilter) {
                filtered = filtered.filter(q => q.difficulty === this.selectedDifficultyFilter);
            }
            
            if (this.search) {
                const searchLower = this.search.toLowerCase();
                filtered = filtered.filter(q => 
                    q.question.toLowerCase().includes(searchLower) ||
                    q.category_name.toLowerCase().includes(searchLower) ||
                    q.explanation.toLowerCase().includes(searchLower)
                );
            }
            
            this.filteredQuestions = filtered;
        },

        closeViewDialog() {
            this.viewDialog = false;
            this.viewingQuestion = null;
        },

        closeEditDialog() {
            this.editDialog = false;
            this.editingQuestion = {};
        },

        closeDeleteDialog() {
            this.deleteDialog = false;
            this.deletingQuestion = null;
        },

        showSnackbar(text, color = 'success') {
            this.snackbarText = text;
            this.snackbarColor = color;
            this.snackbar = true;
        },

        truncateText(text, length) {
            return text.length > length ? text.substring(0, length) + '...' : text;
        },

        getDifficultyColor(difficulty) {
            switch (difficulty) {
                case 'easy': return 'success';
                case 'medium': return 'warning';
                case 'hard': return 'error';
                default: return 'grey';
            }
        },

        getDifficultyText(difficulty) {
            switch (difficulty) {
                case 'easy': return 'Dễ';
                case 'medium': return 'Trung bình';
                case 'hard': return 'Khó';
                default: return difficulty;
            }
        }
    }
}
</script>

<style scoped>
.question-column {
    max-width: 300px;
    word-wrap: break-word;
}

pre {
    white-space: pre-wrap;
    word-wrap: break-word;
}
</style> 